import axios from 'axios';

import { StandaloneAttachment } from '../../entities/standalone-attachment';
import { StandaloneChangelogEntry } from '../../entities/standalone-changelog';
import { StandaloneComment } from '../../entities/standalone-comment';
import { StandaloneInvitation } from '../../entities/standalone-invitation';
import { StandaloneProject } from '../../entities/standalone-project';
import { StandaloneTask, StandaloneTaskMinimal, StandaloneTaskPriority } from '../../entities/standalone-task';
import { auth } from '../firebase/firebase';
import { API_BASE_URL } from './index';

const getHeaders = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

// The status/label/subtask 400s (standalone-contract.md §0 "Error responses") are already
// plain-language and meant to be shown directly — e.g. "3 task(s) use this status — pass
// reassignToStatusId to move them first". Callers show this to the user instead of a generic
// "failed to X" message when it's present.
export const standaloneErrorMessage = (err: unknown): string | undefined =>
  axios.isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;

export const listStandaloneProjects = async (): Promise<StandaloneProject[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ projects: StandaloneProject[] }>(`${API_BASE_URL}/api/standalone/projects`, {
    headers,
  });
  return data.projects;
};

// `key` is optional — omit it and the server auto-derives one from `name` (collision-disambiguated).
// Pass it explicitly to let the user pick their own; it must be `^[A-Z][A-Z0-9]{1,9}$` and can
// never be changed after creation (standalone-contract.md §0 "Readable keys").
export const createStandaloneProject = async (
  name: string,
  description?: string,
  key?: string,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects`,
    { name, description, key: key || undefined },
    { headers },
  );
  return data;
};

export const getStandaloneProject = async (projectId: string): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.get<StandaloneProject>(`${API_BASE_URL}/api/standalone/projects/${projectId}`, {
    headers,
  });
  return data;
};

export const updateStandaloneProject = async (
  projectId: string,
  patch: { name?: string; description?: string },
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<StandaloneProject>(`${API_BASE_URL}/api/standalone/projects/${projectId}`, patch, {
    headers,
  });
  return data;
};

export const deleteStandaloneProject = async (projectId: string): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/standalone/projects/${projectId}`, { headers });
};

// --- Sharing (MF-57/MF-58) ---

export const inviteStandaloneMember = async (
  projectId: string,
  email: string,
  role: 'owner' | 'member',
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/members`,
    { email, role },
    { headers },
  );
  return data;
};

// Exactly one of userId/email identifies the target — a member removing themselves ("Leave") is
// the same endpoint as an owner removing someone else, just targeting your own id/email.
export const removeStandaloneMember = async (
  projectId: string,
  target: { userId?: string; email?: string },
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.delete<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/members`,
    { headers, data: target },
  );
  return data;
};

export const listStandaloneInvitations = async (): Promise<StandaloneInvitation[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ invitations: StandaloneInvitation[] }>(
    `${API_BASE_URL}/api/standalone/invitations`,
    { headers },
  );
  return data.invitations;
};

export const acceptStandaloneInvitation = async (projectId: string): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/invitations/${projectId}/accept`,
    {},
    { headers },
  );
  return data;
};

export const declineStandaloneInvitation = async (projectId: string): Promise<{ success: boolean }> => {
  const headers = await getHeaders();
  const { data } = await axios.post<{ success: boolean }>(
    `${API_BASE_URL}/api/standalone/invitations/${projectId}/decline`,
    {},
    { headers },
  );
  return data;
};

// --- Statuses (per project) ---

export interface StandaloneStatusInput {
  name: string;
  category: 'todo' | 'in_progress' | 'done';
  color?: string;
  // MF-39 — 'working'/'review' sets the coding-agent role; null (PATCH only) clears it.
  agentRole?: 'working' | 'review' | null;
}

export const createStandaloneStatus = async (
  projectId: string,
  input: StandaloneStatusInput,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/statuses`,
    input,
    { headers },
  );
  return data;
};

export const updateStandaloneStatus = async (
  projectId: string,
  statusId: string,
  patch: Partial<StandaloneStatusInput>,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/statuses/${statusId}`,
    patch,
    { headers },
  );
  return data;
};

export const deleteStandaloneStatus = async (
  projectId: string,
  statusId: string,
  reassignToStatusId?: string,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.delete<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/statuses/${statusId}`,
    { headers, data: reassignToStatusId ? { reassignToStatusId } : undefined },
  );
  return data;
};

export const reorderStandaloneStatuses = async (projectId: string, statusIds: string[]): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/statuses/reorder`,
    { statusIds },
    { headers },
  );
  return data;
};

// --- Labels (per project) ---

export interface StandaloneLabelInput {
  name: string;
  color?: string;
}

export const createStandaloneLabel = async (
  projectId: string,
  input: StandaloneLabelInput,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/labels`,
    input,
    { headers },
  );
  return data;
};

export const updateStandaloneLabel = async (
  projectId: string,
  labelId: string,
  patch: Partial<StandaloneLabelInput>,
): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/labels/${labelId}`,
    patch,
    { headers },
  );
  return data;
};

export const deleteStandaloneLabel = async (projectId: string, labelId: string): Promise<StandaloneProject> => {
  const headers = await getHeaders();
  const { data } = await axios.delete<StandaloneProject>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/labels/${labelId}`,
    { headers },
  );
  return data;
};

// --- Tasks ---

export const listStandaloneTasks = async (projectId: string): Promise<StandaloneTask[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ tasks: StandaloneTask[] }>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/tasks`,
    { headers },
  );
  return data.tasks;
};

// Direct children only, full task shape — for a task's own subtasks list without pulling the
// whole project's tasks. On a branch not yet merged to develop as of adding this (mflow-msa-mono
// feat/standalone-lightweight-task-list) — confirm it's live if this 404s.
export const listStandaloneSubtasks = async (projectId: string, parentId: string): Promise<StandaloneTask[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ tasks: StandaloneTask[] }>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/tasks/by-parent/${parentId}`,
    { headers },
  );
  return data.tasks;
};

// Every task in the project, but only {_id, title, key, parentId} — for the parent-task picker,
// which needs the whole project to walk/exclude a subtree but never needed the other fields
// (description, labels, priority, timestamps, ...) it used to pull in via listStandaloneTasks.
// Same not-yet-merged branch as listStandaloneSubtasks.
export const listStandaloneTasksMinimal = async (projectId: string): Promise<StandaloneTaskMinimal[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ tasks: StandaloneTaskMinimal[] }>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/tasks/minimal`,
    { headers },
  );
  return data.tasks;
};

// The other direction from `task.key` — resolves a readable key (e.g. from a shared "WR-12" URL)
// to the full task, `_id` included. `key` isn't accepted anywhere else a `taskId` is expected.
export const getStandaloneTaskByKey = async (key: string): Promise<StandaloneTask> => {
  const headers = await getHeaders();
  const { data } = await axios.get<StandaloneTask>(
    `${API_BASE_URL}/api/standalone/tasks/by-key/${encodeURIComponent(key)}`,
    { headers },
  );
  return data;
};

// Single task by internal id, full fields — the id-based counterpart to getStandaloneTaskByKey.
// Used wherever a caller only has `_id` (e.g. CodeAgentTask.standaloneTaskId) and wherever a full
// task's details are needed but only a slimmer list-endpoint object is already in hand (the board
// list is expected to eventually shed detail fields it doesn't need for rendering cards).
export const getStandaloneTask = async (taskId: string): Promise<StandaloneTask> => {
  const headers = await getHeaders();
  const { data } = await axios.get<StandaloneTask>(`${API_BASE_URL}/api/standalone/tasks/${taskId}`, { headers });
  return data;
};

// Drag-and-drop reordering. `taskIds` is the complete, authoritative new ordered list for one
// column (`status`) — the server sets each task's `order` to its array index and its `status` to
// the given column, so a cross-column drag is just: include the moved task in its destination
// column's `taskIds`. Tasks not mentioned are left alone (see standalone-contract.md "Task
// ordering"). Returns only the tasks that were part of the reorder, in the new order.
export const reorderStandaloneTasks = async (
  projectId: string,
  status: string,
  taskIds: string[],
): Promise<StandaloneTask[]> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<{ tasks: StandaloneTask[] }>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/tasks/reorder`,
    { status, taskIds },
    { headers },
  );
  return data.tasks;
};

export interface StandaloneTaskInput {
  title: string;
  description?: string;
  status?: string;
  priority?: StandaloneTaskPriority;
  dueDate?: string;
  parentId?: string;
  labelIds?: string[];
}

export const createStandaloneTask = async (projectId: string, input: StandaloneTaskInput): Promise<StandaloneTask> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneTask>(
    `${API_BASE_URL}/api/standalone/projects/${projectId}/tasks`,
    input,
    { headers },
  );
  return data;
};

export const updateStandaloneTask = async (
  taskId: string,
  patch: Partial<StandaloneTaskInput>,
): Promise<StandaloneTask> => {
  const headers = await getHeaders();
  const { data } = await axios.patch<StandaloneTask>(`${API_BASE_URL}/api/standalone/tasks/${taskId}`, patch, {
    headers,
  });
  return data;
};

export const deleteStandaloneTask = async (taskId: string): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/standalone/tasks/${taskId}`, { headers });
};

// --- Comments (per task) ---
// Add/read/delete only — no edit route in v1 (standalone-contract.md §0 "Comments").

export const listStandaloneComments = async (taskId: string): Promise<StandaloneComment[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ comments: StandaloneComment[] }>(
    `${API_BASE_URL}/api/standalone/tasks/${taskId}/comments`,
    { headers },
  );
  return data.comments;
};

export const createStandaloneComment = async (taskId: string, text: string): Promise<StandaloneComment> => {
  const headers = await getHeaders();
  const { data } = await axios.post<StandaloneComment>(
    `${API_BASE_URL}/api/standalone/tasks/${taskId}/comments`,
    { text },
    { headers },
  );
  return data;
};

export const deleteStandaloneComment = async (taskId: string, commentId: string): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/standalone/tasks/${taskId}/comments/${commentId}`, { headers });
};

// --- Changelog (per task, read-only) ---
// No historical backfill — edits made before this feature deployed have no entries (MVPB-25).

export const listStandaloneChangelog = async (taskId: string): Promise<StandaloneChangelogEntry[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ entries: StandaloneChangelogEntry[] }>(
    `${API_BASE_URL}/api/standalone/tasks/${taskId}/changelog`,
    { headers },
  );
  return data.entries;
};

// --- Attachments (per task) --- MVPB-63
// No WS live-update yet (docs/fe-contracts/standalone-contract.md) — callers must locally
// append/remove on upload/delete rather than expecting a socket event.

export const listStandaloneAttachments = async (taskId: string): Promise<StandaloneAttachment[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ attachments: StandaloneAttachment[] }>(
    `${API_BASE_URL}/api/standalone/tasks/${taskId}/attachments`,
    { headers },
  );
  return data.attachments;
};

export const uploadStandaloneAttachment = async (taskId: string, file: File): Promise<StandaloneAttachment> => {
  const headers = await getHeaders();
  const formData = new FormData();
  formData.append('file', file);
  // Don't set Content-Type manually — axios/the browser needs to compute the multipart boundary
  // itself from the FormData body; overriding it here would send a boundary-less header and break
  // parsing server-side.
  const { data } = await axios.post<StandaloneAttachment>(
    `${API_BASE_URL}/api/standalone/tasks/${taskId}/attachments`,
    formData,
    { headers },
  );
  return data;
};

export const deleteStandaloneAttachment = async (taskId: string, attachmentId: string): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/standalone/tasks/${taskId}/attachments/${attachmentId}`, { headers });
};
